import { useEffect, useRef, useState } from 'react';
import { useTTSStore } from '../../store/tts';
import clsx from 'clsx';

function VoiceSelector() {
  const { voices, selectedVoice, selectVoice, fetchVoices, isLoadingVoices } = useTTSStore();
  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const [loadingVoiceId, setLoadingVoiceId] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchVoices();
  }, [fetchVoices]);

  // Clean up audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handlePreviewVoice = async (e, voice) => {
    e.stopPropagation(); // Prevent selecting the voice

    // If already playing this voice, stop it
    if (playingVoiceId === voice.voice_id) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingVoiceId(null);
      return;
    }

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    setLoadingVoiceId(voice.voice_id);
    
    try {
      // Use cached preview endpoint - much faster than generating via TTS
      const { ttsService } = await import('../../services/tts');
      const audioUrl = await ttsService.getVoicePreview(voice.voice_id);
      
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      
      audio.oncanplaythrough = () => {
        setLoadingVoiceId(null);
        setPlayingVoiceId(voice.voice_id);
        audio.play();
      };
      
      audio.onended = () => {
        setPlayingVoiceId(null);
        audioRef.current = null;
        URL.revokeObjectURL(audioUrl);
      };
      
      audio.onerror = () => {
        setLoadingVoiceId(null);
        setPlayingVoiceId(null);
        audioRef.current = null;
      };
      
      audio.load();
    } catch (error) {
      console.error('Failed to play preview:', error);
      setLoadingVoiceId(null);
    }
  };

  if (isLoadingVoices) {
    return (
      <div className="card">
        <h3 className="font-semibold mb-4">Select Voice</h3>
        <div className="flex items-center justify-center py-8">
          <span className="spinner w-8 h-8"></span>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <h3 className="font-semibold mb-4">Select Voice</h3>
      
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {voices.length === 0 ? (
          <p className="text-gray-400 text-sm">No voices available</p>
        ) : (
          voices.map((voice) => (
            <button
              key={voice.id}
              onClick={() => selectVoice(voice)}
              className={clsx(
                'w-full p-3 rounded-lg text-left transition-all',
                selectedVoice?.id === voice.id
                  ? 'bg-purple-600/20 border border-purple-500'
                  : 'bg-gray-700/50 border border-transparent hover:bg-gray-700'
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{voice.name}</p>
                  <p className="text-sm text-gray-400 truncate">
                    {voice.language_name}
                    {voice.gender && ` • ${voice.gender}`}
                    {voice.accent && ` • ${voice.accent}`}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-2 shrink-0">
                  {/* Preview/Play button */}
                  <button
                    onClick={(e) => handlePreviewVoice(e, voice)}
                    className={clsx(
                      'p-2 rounded-full transition-all',
                      playingVoiceId === voice.voice_id
                        ? 'bg-purple-500 text-white'
                        : 'bg-gray-600 hover:bg-gray-500 text-gray-300 hover:text-white'
                    )}
                    title={playingVoiceId === voice.voice_id ? 'Stop preview' : 'Preview voice'}
                    disabled={loadingVoiceId === voice.voice_id}
                  >
                    {loadingVoiceId === voice.voice_id ? (
                      <span className="spinner w-4 h-4"></span>
                    ) : playingVoiceId === voice.voice_id ? (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <rect x="6" y="6" width="4" height="12" />
                        <rect x="14" y="6" width="4" height="12" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    )}
                  </button>
                  {voice.is_cloned && (
                    <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs rounded">
                      Cloned
                    </span>
                  )}
                  {voice.is_premium && !voice.is_cloned && (
                    <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded">
                      Premium
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

export default VoiceSelector;
